import {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import BpmnModeler from "bpmn-js/lib/Modeler";
import { toast } from "sonner";
import "bpmn-js/dist/assets/diagram-js.css";
import "bpmn-js/dist/assets/bpmn-font/css/bpmn.css";
import "@bpmn-io/properties-panel/dist/assets/properties-panel.css";
import "../../styles/properties-panel.css";
import {
  X,
  LayoutGrid,
  Plus,
  Minus,
  Search,
  Undo,
  Redo,
  Diamond,
  Columns,
  Settings,
  Pin,
} from "lucide-react";

import {
  BpmnPropertiesPanelModule,
  BpmnPropertiesProviderModule,
  CamundaPlatformPropertiesProviderModule,
} from "bpmn-js-properties-panel";
import camundaModdleDescriptor from "camunda-bpmn-moddle/resources/camunda.json";
import { ElementTemplateIconRenderer } from "../../bpmn/CustomElementTemplateIconRenderer/ElementTemplateIconRenderer";
import { registerTemplateIcon } from "../../bpmn/CustomElementTemplateIconRenderer";
import CustomContextPadModule from "../../bpmn/CustomContextPad";
import CustomPropertiesPanel from "./CustomPropertiesPanel";

/**
 * Strip non-standard Camunda attributes that older versions of this editor
 * baked into the XML. Both attributes cause Camunda Engine 7.x to log
 * "unknown attribute" warnings on deploy — and some deployments treat them
 * as fatal. The icon is restored at render time from a module-level
 * registry keyed by `camunda:modelerTemplate`, so dropping it from the XML
 * is safe.
 *
 * Done as a regex pass on the serialized output rather than mutating the
 * moddle tree because the bad attributes can also slip in via copy/paste
 * of legacy diagrams; this catches every case in one place.
 */
function sanitizeBpmnXml(xml: string): string {
  return xml.replace(
    /\s+camunda:(?:customElement|modelerTemplateIcon)="[^"]*"/g,
    "",
  );
}

/**
 * Heal legacy BPMN where a connector-template task (e.g. `conn-document-ai`)
 * was saved without the required `<camunda:connector>` wrapper around its
 * `<camunda:inputOutput>`. Diagrams in that broken state deploy to
 * Camunda 7.x with `ENGINE-09005` ("class/delegateExpression/type/expression
 * mandatory on serviceTask") because the engine has nothing executable to
 * hand the task off to.
 *
 * We detect the broken shape by looking for any `<*:serviceTask>` that:
 *   • carries `camunda:modelerTemplate="conn-..."` (legacy connector tag), AND
 *   • has an `<*:extensionElements>` containing a direct
 *     `<camunda:inputOutput>` with no surrounding `<camunda:connector>`.
 *
 * For each match we wrap the inputOutput in
 *   `<camunda:connector>` + `<camunda:connectorId>http-connector</camunda:connectorId>`.
 * The transformation runs as a DOMParser pass on the XML string, *before*
 * `modeler.importXML(...)`, so the moddle tree starts out clean and any
 * subsequent save round-trips the corrected shape.
 *
 * Java-delegate templates (anything with `camunda:class=...`) are untouched.
 */
function migrateLegacyConnectorTemplates(xml: string): string {
  if (!xml || typeof window === "undefined" || !window.DOMParser) return xml;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, "application/xml");
    if (doc.getElementsByTagName("parsererror").length > 0) return xml;

    // bpmn-js accepts both `bpmn:` and `bpmn2:` prefixes for the BPMN
    // namespace; match the element regardless of the prefix the file uses.
    const BPMN_NS = "http://www.omg.org/spec/BPMN/20100524/MODEL";
    const CAMUNDA_NS = "http://camunda.org/schema/1.0/bpmn";
    const serviceTasks = Array.from(
      doc.getElementsByTagNameNS(BPMN_NS, "serviceTask"),
    );

    let mutated = false;
    for (const task of serviceTasks) {
      // A task is a connector-template candidate if it carries either the
      // new `camunda:modelerTemplate` or the legacy `camunda:customElement`
      // with a `conn-*` value. Older saves only have the latter; if we
      // ignored it the migration would skip those tasks, the on-save
      // sanitizer would then strip `customElement`, and the next reload
      // would leave the task with NO template tag — which is why the
      // properties panel couldn't find a schema match. We unify both into
      // the schema-stable `camunda:modelerTemplate` here.
      const tplNew = task.getAttributeNS(CAMUNDA_NS, "modelerTemplate") || "";
      const tplLegacy = task.getAttributeNS(CAMUNDA_NS, "customElement") || "";
      const tpl = tplNew || tplLegacy;

      // Diagnostic — temporary. Tells us exactly what the loaded XML
      // carries on each service task and whether the migration matched.
      // Remove once the panel + deploy chain is verified end-to-end.
      // eslint-disable-next-line no-console
      console.log(
        "[migrateLegacyConnectorTemplates]",
        task.getAttribute("id"),
        { tplNew, tplLegacy, tpl, willMigrate: tpl.startsWith("conn-") },
      );

      const hasExec =
        task.hasAttributeNS(CAMUNDA_NS, "class") ||
        task.hasAttributeNS(CAMUNDA_NS, "delegateExpression") ||
        task.hasAttributeNS(CAMUNDA_NS, "type") ||
        task.hasAttributeNS(CAMUNDA_NS, "expression");
      if (hasExec) continue;
      if (!tpl.startsWith("conn-")) continue;

      // Always promote legacy `customElement` to `modelerTemplate` so
      // both the engine deploy path and the properties panel can read
      // the same attribute going forward. (Older diagrams: this is the
      // only thing keeping the template association alive once the
      // sanitizer strips `customElement` on save.)
      if (!tplNew && tplLegacy) {
        task.setAttributeNS(
          CAMUNDA_NS,
          "camunda:modelerTemplate",
          tplLegacy,
        );
        mutated = true;
      }

      const extElements = task.getElementsByTagNameNS(
        BPMN_NS,
        "extensionElements",
      )[0];
      if (!extElements) continue;

      const directInputOutput = Array.from(extElements.children).find(
        (c) =>
          c.namespaceURI === CAMUNDA_NS && c.localName === "inputOutput",
      );
      // Already wrapped? Nothing to do for the connector portion.
      if (!directInputOutput) continue;

      // Build `<camunda:connector><camunda:inputOutput .../>
      //         <camunda:connectorId>http-connector</camunda:connectorId>
      //       </camunda:connector>` and swap it in.
      const connector = doc.createElementNS(CAMUNDA_NS, "camunda:connector");
      const connectorId = doc.createElementNS(
        CAMUNDA_NS,
        "camunda:connectorId",
      );
      connectorId.textContent = "http-connector";
      connector.appendChild(directInputOutput);
      connector.appendChild(connectorId);
      extElements.appendChild(connector);
      mutated = true;
    }

    if (!mutated) return xml;
    return new XMLSerializer().serializeToString(doc);
  } catch (e) {
    console.warn(
      "migrateLegacyConnectorTemplates: skipped due to parse error",
      e,
    );
    return xml;
  }
}

import {
  getWorkflowContent,
  saveWorkflowContent,
  decodeBpmnXML,
  emptyBpmnTemplate,
  generateBpmnTemplate,
  getDesignerTemplates,
} from "../../services/workflowService";
import type {
  Workflow,
  DesignerTemplate,
} from "../../services/workflowService";

interface SimpleBpmnEditorProps {
  workflow?: Workflow | null;
  onSave?: (workflow: Workflow) => void;
}

export interface SimpleBpmnEditorRef {
  save: () => Promise<void>;
  getXml: () => Promise<string | undefined>;
  refresh: () => void;
  importXml: (xml: string) => Promise<void>;
  exportXml: () => void;
  isSaving: boolean;
}

const SimpleBpmnEditor = forwardRef<SimpleBpmnEditorRef, SimpleBpmnEditorProps>(
  ({ workflow, onSave }, ref) => {
    const canvasRef = useRef<HTMLDivElement>(null);
    const propertiesPanelRef = useRef<HTMLDivElement>(null);
    const modelerRef = useRef<BpmnModeler | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [zoomLevel, setZoomLevel] = useState(100);
    const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(true);
    const [activeTab, setActiveTab] = useState<
      "addObject" | "customConnectors"
    >("addObject");
    const [searchQuery, setSearchQuery] = useState("");
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
    const [isRightPanelOpen, setIsRightPanelOpen] = useState(true);
    const [connectorTemplates, setConnectorTemplates] = useState<
      DesignerTemplate[]
    >([]);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
    const [selectedElement, setSelectedElement] = useState<any>(null);
    const [rightPanelTab, setRightPanelTab] = useState<
      "properties" | "element"
    >("properties");
    const [refreshKey, setRefreshKey] = useState(0);

    // Pinned Connectors State
    const [pinnedConnectorIds, setPinnedConnectorIds] = useState<string[]>(
      () => {
        try {
          const saved = localStorage.getItem("pinnedConnectorIds");
          return saved ? JSON.parse(saved) : [];
        } catch (e) {
          return [];
        }
      },
    );

    const toggleConnectorPin = (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setPinnedConnectorIds((prev) => {
        const next = prev.includes(id)
          ? prev.filter((p) => p !== id)
          : [...prev, id];
        localStorage.setItem("pinnedConnectorIds", JSON.stringify(next));
        return next;
      });
    };

    /**
     * Returns true if the selected element should render the custom connector
     * properties tab. Recognises both freshly-dropped tasks (which carry the
     * in-memory `custom:properties` attribute) and reloaded tasks (which only
     * have a registered Camunda implementation attribute, since `custom:` is
     * stripped on serialization).
     */
    const isConnectorTask = (el: any): boolean => {
      if (!el?.businessObject) return false;
      const bo = el.businessObject;
      if (bo.$type !== "bpmn:ServiceTask") return false;
      const get = (k: string) => {
        try {
          return bo.get?.(k);
        } catch {
          return undefined;
        }
      };
      const attrs = bo.$attrs || {};

      // Java-delegate templates and tasks that were saved with an
      // executable attribute on the businessObject directly.
      const hasDelegate = !!(
        get("custom:properties") ||
        get("camunda:class") ||
        get("camunda:delegateExpression") ||
        get("camunda:expression") ||
        bo.class ||
        bo.delegateExpression ||
        bo.expression ||
        attrs["camunda:class"] ||
        attrs["camunda:delegateExpression"] ||
        attrs["camunda:expression"]
      );
      if (hasDelegate) return true;

      // Templated tasks (both Java-delegate AND connector-style). Once a
      // task was created from an element template, it carries
      // `camunda:modelerTemplate` (new) or `camunda:customElement` (legacy
      // pre-fix attribute). Without this branch a saved connector task
      // — which has neither `camunda:class` nor `custom:properties` —
      // fails the gate and the Document AI / connector tab never mounts.
      const templateTag =
        get("camunda:modelerTemplate") ||
        get("camunda:customElement") ||
        attrs["camunda:modelerTemplate"] ||
        attrs["camunda:customElement"];
      if (templateTag) return true;

      // Connector-bearing tasks (e.g. those wrapped with
      // `<camunda:connector><camunda:connectorId>http-connector</...>`)
      // — Camunda Connect plugin executes them. The properties panel
      // should treat them the same as any other connector task.
      const ext = (bo.extensionElements || get("bpmn:extensionElements")) as
        | { values?: Array<{ $type?: string }> }
        | undefined;
      const hasConnector =
        Array.isArray(ext?.values) &&
        ext!.values!.some((v) => v?.$type === "camunda:Connector");
      if (hasConnector) return true;

      return false;
    };

    // Get the slug to use for API calls
    const getSlug = (): string | null => {
      if (workflow?.nameSlug) {
        return workflow.nameSlug;
      }
      if (workflow?.name) {
        return workflow.name.toLowerCase().replace(/\s+/g, "_");
      }
      return null;
    };

    // Initialize modeler
    useEffect(() => {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(async () => {
        if (!canvasRef.current) {
          console.error(
            "SimpleBpmnEditor: Canvas ref still not available after delay",
          );
          setError("Canvas element not available");
          setIsLoading(false);
          return;
        }

        console.log("SimpleBpmnEditor: Starting initialization...");

        try {
          // Destroy previous modeler if exists
          if (modelerRef.current) {
            modelerRef.current.destroy();
            modelerRef.current = null;
          }

          // Create modeler with properties panel
          const modeler = new BpmnModeler({
            container: canvasRef.current,
            propertiesPanel: {
              parent: propertiesPanelRef.current,
            },
            additionalModules: [
              BpmnPropertiesPanelModule,
              BpmnPropertiesProviderModule,
              CamundaPlatformPropertiesProviderModule,
              // Custom renderer for template icons
              {
                __init__: ["elementTemplateIconRenderer"],
                elementTemplateIconRenderer: [
                  "type",
                  ElementTemplateIconRenderer,
                ],
              },
              // Custom context pad positioning (above element)
              CustomContextPadModule,
              // Disable bpmn.io logo
              {
                __init__: ["disablePoweredBy"],
                disablePoweredBy: [
                  "type",
                  function () {
                    return function () {
                      /* Disable logo */
                    };
                  },
                ],
              },
            ],
            moddleExtensions: {
              camunda: camundaModdleDescriptor,
            },
          });

          modelerRef.current = modeler;
          console.log(
            "SimpleBpmnEditor: Modeler created with properties panel",
          );

          // Determine BPMN XML to load
          const slug = getSlug();
          const version = workflow?.version || "0.1";
          const triggerType = workflow?.triggerType || "Manual";

          // Default to generated template with proper process ID (nameSlug_randomId) and trigger-based start event
          let bpmnXml = slug
            ? generateBpmnTemplate(slug, version, triggerType)
            : emptyBpmnTemplate;

          console.log("SimpleBpmnEditor: Workflow slug:", slug);

          if (slug) {
            try {
              console.log("SimpleBpmnEditor: Fetching content from API...");
              const content = await getWorkflowContent(slug);
              console.log(
                "SimpleBpmnEditor: Content received:",
                content ? "yes" : "no",
              );

              if (content?.bpmn) {
                bpmnXml = decodeBpmnXML(content.bpmn);
                console.log(
                  "SimpleBpmnEditor: Decoded XML, length:",
                  bpmnXml.length,
                );
              }
            } catch (apiError) {
              console.log(
                "SimpleBpmnEditor: No existing BPMN content, using generated template with ID:",
                slug,
              );
            }
          }

          // Import the diagram. Heal any legacy connector-template tasks
          // that were saved without the `<camunda:connector>` wrapper —
          // see migrateLegacyConnectorTemplates for the full rationale.
          // Without this, existing `conn-*` diagrams continue to fail
          // deploy with ENGINE-09005 until the user manually re-applies
          // the template.
          const xmlForImport = migrateLegacyConnectorTemplates(bpmnXml);
          console.log("SimpleBpmnEditor: Importing XML...");
          await modeler.importXML(xmlForImport);
          console.log("SimpleBpmnEditor: XML imported successfully");

          // Zoom to fit
          const canvas = modeler.get("canvas") as any;
          canvas.zoom("fit-viewport", "auto");

          // Listen for selection changes to update custom properties panel
          const eventBus = modeler.get("eventBus") as any;
          eventBus.on("selection.changed", (e: any) => {
            const { newSelection } = e;
            if (newSelection && newSelection.length === 1) {
              setSelectedElement(newSelection[0]);
            } else {
              setSelectedElement(null);
            }
          });

          // Done loading
          console.log("SimpleBpmnEditor: Setting isLoading to false");
          setIsLoading(false);
          toast.success("BPMN Editor loaded!");
        } catch (err) {
          console.error("SimpleBpmnEditor: Error:", err);
          const errorMessage =
            err instanceof Error ? err.message : "Failed to load BPMN editor";
          setError(errorMessage);
          setIsLoading(false);
          toast.error(errorMessage);
        }
      }, 100); // Small delay to ensure canvas is rendered

      return () => {
        clearTimeout(timer);
        if (modelerRef.current) {
          modelerRef.current.destroy();
          modelerRef.current = null;
        }
      };
    }, [workflow?.id, refreshKey]); // Re-run when workflow ID or refresh key changes

    const handleRefresh = useCallback(() => {
      setRefreshKey((prev) => prev + 1);
    }, []);

    const incrementVersion = (version: string): string => {
      // Default to a safe starting version if undefined
      if (!version) return "0.1";

      let [majorStr, minorStr] = version.split(".");
      // Handle cases like "1" becoming "1.0" first or handle parsing
      let major = parseInt(majorStr) || 0;
      let minor = parseInt(minorStr) || 0;

      // Increment the minor version
      minor += 1;

      // Check if minor version exceeds 9
      if (minor > 9) {
        minor = 0;
        major += 1;
      }

      // Return the new version as a string
      return `${major}.${minor}`;
    };

    // Save diagram function
    const handleSave = async () => {
      if (!modelerRef.current || !workflow) {
        toast.error("Cannot save: no workflow selected");
        return;
      }

      try {
        setIsSaving(true);

        const { xml } = await modelerRef.current.saveXML({ format: true });
        if (!xml) {
          throw new Error("Failed to export BPMN XML");
        }
        // Drop legacy `camunda:customElement` / `camunda:modelerTemplateIcon`
        // so the engine doesn't warn on deploy.
        const cleanXml = sanitizeBpmnXml(xml);

        const currentVersion = workflow.version || "0.0";
        const newVersion = incrementVersion(currentVersion);

        const slug = getSlug() || "workflow";
        const savedContent = await saveWorkflowContent(
          slug,
          newVersion,
          cleanXml,
          workflow,
        );

        toast.success(`Workflow saved (v${newVersion})`);

        if (onSave && savedContent.process) {
          onSave(savedContent.process);
        }
      } catch (err) {
        console.error("Error saving workflow:", err);
        toast.error("Failed to save workflow");
      } finally {
        setIsSaving(false);
      }
    };

    // Get XML function for deployment
    const handleGetXml = async (): Promise<string | undefined> => {
      if (!modelerRef.current) return undefined;
      try {
        const { xml } = await modelerRef.current.saveXML({ format: true });
        // Strip the legacy non-standard Camunda attributes before handing
        // the XML to deployment; the engine rejects them in strict mode.
        return xml ? sanitizeBpmnXml(xml) : xml;
      } catch (e) {
        console.error("Error exporting XML", e);
        return undefined;
      }
    };

    // Zoom controls
    const handleZoomIn = () => {
      if (modelerRef.current) {
        const canvas = modelerRef.current.get("canvas") as any;
        const newZoom = Math.min(zoomLevel + 10, 200);
        canvas.zoom(newZoom / 100);
        setZoomLevel(newZoom);
      }
    };

    const handleZoomOut = () => {
      if (modelerRef.current) {
        const canvas = modelerRef.current.get("canvas") as any;
        const newZoom = Math.max(zoomLevel - 10, 50);
        canvas.zoom(newZoom / 100);
        setZoomLevel(newZoom);
      }
    };

    // Undo/Redo
    const handleUndo = () => {
      if (modelerRef.current) {
        const commandStack = modelerRef.current.get("commandStack") as any;
        commandStack.undo();
      }
    };

    const handleRedo = () => {
      if (modelerRef.current) {
        const commandStack = modelerRef.current.get("commandStack") as any;
        commandStack.redo();
      }
    };

    // Fetch designer templates for connectors tab
    useEffect(() => {
      const fetchTemplates = async () => {
        setIsLoadingTemplates(true);
        try {
          const templates = await getDesignerTemplates();
          setConnectorTemplates(templates);

          // Pre-populate the icon registry from every loaded template so
          // existing tasks already-tagged with `camunda:modelerTemplate`
          // render their icons immediately after a page refresh — without
          // this the registry only fills when a template is *applied*,
          // and a fresh page load with an existing diagram (e.g.
          // `/studio/dai-process`) shows blank service-task tiles.
          for (const t of templates) {
            const topic = t?.decodedContent?.topic;
            const iconUrl = t?.decodedContent?.modelerTemplateIcon;
            if (typeof topic === "string" && typeof iconUrl === "string") {
              registerTemplateIcon(topic, iconUrl);
            }
          }
        } catch (err) {
          console.error("Error fetching designer templates:", err);
        } finally {
          setIsLoadingTemplates(false);
        }
      };

      fetchTemplates();
    }, []);

    // Create BPMN elements
    const createBpmnElement = (type: string) => {
      if (!modelerRef.current) return;

      const modeling = modelerRef.current.get("modeling") as any;
      const elementFactory = modelerRef.current.get("elementFactory") as any;
      const canvas = modelerRef.current.get("canvas") as any;

      // Get the root element (process)
      const rootElement = canvas.getRootElement();

      // Get the viewport center for placement
      const viewbox = canvas.viewbox();
      const centerX = viewbox.x + viewbox.width / 2;
      const centerY = viewbox.y + viewbox.height / 2;

      // Create the shape
      const shape = elementFactory.createShape({ type });

      // Add some randomness to position to avoid overlapping
      const offsetX = (Math.random() - 0.5) * 100;
      const offsetY = (Math.random() - 0.5) * 100;

      // Create the element on the canvas
      modeling.createShape(
        shape,
        { x: centerX + offsetX, y: centerY + offsetY },
        rootElement,
      );

      // Select the newly created element
      const selection = modelerRef.current.get("selection") as any;
      selection.select(shape);

      toast.success(`Added ${type.replace("bpmn:", "")}`);
    };

    // Create BPMN event with specific event definition
    const createEventWithDefinition = (
      eventType: string,
      definitionType?: string,
    ) => {
      if (!modelerRef.current) return;

      const modeling = modelerRef.current.get("modeling") as any;
      const elementFactory = modelerRef.current.get("elementFactory") as any;
      const canvas = modelerRef.current.get("canvas") as any;
      const bpmnFactory = modelerRef.current.get("bpmnFactory") as any;

      // Get the root element (process)
      const rootElement = canvas.getRootElement();

      // Get the viewport center for placement
      const viewbox = canvas.viewbox();
      const centerX = viewbox.x + viewbox.width / 2;
      const centerY = viewbox.y + viewbox.height / 2;

      // Generate a proper ID that includes the event type for CSS targeting
      const eventTypeName = eventType.replace("bpmn:", "");
      const uniqueId = `${eventTypeName}_${Math.random().toString(36).substring(2, 9)}`;

      // Create the business object with event definition if specified
      let businessObject;
      if (definitionType) {
        const eventDefinition = bpmnFactory.create(definitionType);
        businessObject = bpmnFactory.create(eventType, {
          id: uniqueId,
          eventDefinitions: [eventDefinition],
        });
      } else {
        businessObject = bpmnFactory.create(eventType, {
          id: uniqueId,
        });
      }

      // Create the shape
      const shape = elementFactory.createShape({
        type: eventType,
        businessObject: businessObject,
      });

      // Add some randomness to position to avoid overlapping
      const offsetX = (Math.random() - 0.5) * 100;
      const offsetY = (Math.random() - 0.5) * 100;

      // Create the element on the canvas
      modeling.createShape(
        shape,
        { x: centerX + offsetX, y: centerY + offsetY },
        rootElement,
      );

      // Select the newly created element
      const selection = modelerRef.current.get("selection") as any;
      selection.select(shape);

      const typeName =
        eventTypeName +
        (definitionType
          ? ` (${definitionType.replace("bpmn:", "").replace("EventDefinition", "")})`
          : "");
      toast.success(`Added ${typeName}`);
    };

    // Create Start Events with proper event definitions
    const createMessageStartEvent = () => {
      createEventWithDefinition(
        "bpmn:StartEvent",
        "bpmn:MessageEventDefinition",
      );
      setActiveDropdown(null);
    };
    const createTimerStartEvent = () => {
      createEventWithDefinition("bpmn:StartEvent", "bpmn:TimerEventDefinition");
      setActiveDropdown(null);
    };
    const createConditionalStartEvent = () => {
      createEventWithDefinition(
        "bpmn:StartEvent",
        "bpmn:ConditionalEventDefinition",
      );
      setActiveDropdown(null);
    };
    const createSignalStartEvent = () => {
      createEventWithDefinition(
        "bpmn:StartEvent",
        "bpmn:SignalEventDefinition",
      );
      setActiveDropdown(null);
    };

    // Create End Events with proper event definitions
    const createMessageEndEvent = () => {
      createEventWithDefinition("bpmn:EndEvent", "bpmn:MessageEventDefinition");
      setActiveDropdown(null);
    };
    const createSignalEndEvent = () => {
      createEventWithDefinition("bpmn:EndEvent", "bpmn:SignalEventDefinition");
      setActiveDropdown(null);
    };
    const createTerminateEndEvent = () => {
      createEventWithDefinition(
        "bpmn:EndEvent",
        "bpmn:TerminateEventDefinition",
      );
      setActiveDropdown(null);
    };

    // Create Task
    const createTask = () => {
      createBpmnElement("bpmn:Task");
      setActiveDropdown(null);
    };

    // Create Task Types
    const createUserTask = () => {
      createBpmnElement("bpmn:UserTask");
      setActiveDropdown(null);
    };
    const createServiceTask = () => {
      createBpmnElement("bpmn:ServiceTask");
      setActiveDropdown(null);
    };
    const createSendTask = () => {
      createBpmnElement("bpmn:SendTask");
      setActiveDropdown(null);
    };
    const createReceiveTask = () => {
      createBpmnElement("bpmn:ReceiveTask");
      setActiveDropdown(null);
    };
    const createManualTask = () => {
      createBpmnElement("bpmn:ManualTask");
      setActiveDropdown(null);
    };
    const createBusinessRuleTask = () => {
      createBpmnElement("bpmn:BusinessRuleTask");
      setActiveDropdown(null);
    };
    const createScriptTask = () => {
      createBpmnElement("bpmn:ScriptTask");
      setActiveDropdown(null);
    };
    const createCallActivity = () => {
      createBpmnElement("bpmn:CallActivity");
      setActiveDropdown(null);
    };
    const createSubProcess = () => {
      createBpmnElement("bpmn:SubProcess");
      setActiveDropdown(null);
    };

    // Create Service Task from template with configuration
    const createServiceTaskFromTemplate = (template: DesignerTemplate) => {
      if (!modelerRef.current || !template.decodedContent) {
        createServiceTask();
        return;
      }

      const modeling = modelerRef.current.get("modeling") as any;
      const elementFactory = modelerRef.current.get("elementFactory") as any;
      const canvas = modelerRef.current.get("canvas") as any;
      const bpmnFactory = modelerRef.current.get("bpmnFactory") as any;
      const moddle = modelerRef.current.get("moddle") as any;

      // Get the root element (process)
      const rootElement = canvas.getRootElement();

      // Get the viewport center for placement
      const viewbox = canvas.viewbox();
      const centerX = viewbox.x + viewbox.width / 2;
      const centerY = viewbox.y + viewbox.height / 2;

      // Create the business object with template configuration
      const taskName = template.decodedContent.name || template.name;
      const topic = template.decodedContent.topic || "";
      const implementation = template.decodedContent.implementation || "";
      const iconUrl = template.decodedContent.modelerTemplateIcon || "";
      const properties = template.decodedContent.properties || [];

      // Two kinds of service-task template:
      //   1. Java-delegate templates → set `camunda:class` from
      //      `implementation`, and inputs become a plain
      //      `<camunda:inputOutput>` under `<bpmn:extensionElements>`.
      //   2. Connector templates → no `implementation`; instead the
      //      inputs are wrapped in `<camunda:connector>` + a
      //      `<camunda:connectorId>` so the Camunda Connect plugin
      //      can execute the task at deploy time. Without the wrapper
      //      the engine refuses to deploy with ENGINE-09005
      //      ("class/delegateExpression/type/expression mandatory").
      //
      // Recognised signals for connector mode:
      //   - explicit `decodedContent.connectorId` field on the template
      //   - empty `implementation` + a `topic` prefixed with `conn-`
      //     (back-compat for templates authored before the explicit
      //     field existed — the existing Document AI template uses
      //     `topic: "conn-document-ai"`)
      const explicitConnectorId =
        typeof template.decodedContent.connectorId === "string"
          ? (template.decodedContent.connectorId as string).trim()
          : "";
      const isConnectorTemplate =
        !implementation && (!!explicitConnectorId || topic.startsWith("conn-"));
      // Default HTTP connector for legacy `conn-*` templates that don't
      // declare their connectorId yet. Matches the existing Document AI
      // task shape.
      const connectorId =
        explicitConnectorId || (isConnectorTemplate ? "http-connector" : "");

      const businessObject = bpmnFactory.create("bpmn:ServiceTask", {
        id: `ServiceTask_${Math.random().toString(36).substring(2, 9)}`,
        name: taskName,
      });

      // Set Camunda-specific attributes.
      //
      // We use `camunda:modelerTemplate` (a real attribute in the camunda
      // BPMN moddle schema) to tag the task with the template id. Two
      // attributes were previously written here:
      //   - `camunda:customElement`        ← not in the schema, engine warned
      //   - `camunda:modelerTemplateIcon`  ← bloats XML; some engine builds
      //                                      flag it as unknown and refuse
      //                                      to deploy.
      // Both have been removed in favour of `camunda:modelerTemplate` plus
      // an out-of-band icon registry (see `registerTemplateIcon` below).
      if (topic) {
        businessObject.set("camunda:modelerTemplate", topic);
      }
      if (implementation) {
        businessObject.set("camunda:class", implementation);
      }
      // Register the icon in the renderer's module-level lookup. The
      // renderer reads it by `camunda:modelerTemplate` id, so the icon
      // stays in the modeler UI without ending up in the deployed BPMN.
      if (iconUrl && topic) {
        registerTemplateIcon(topic, iconUrl);
      }

      // Create extension elements with input/output parameters from template properties
      if (properties.length > 0) {
        // Separate properties by parameter type
        const inputParams: any[] = [];
        const outputParams: any[] = [];

        properties.forEach((prop: any) => {
          if (prop.parameterType === "input") {
            const param = moddle.create("camunda:InputParameter", {
              name: prop.id,
              value: prop.default || "",
            });
            inputParams.push(param);
          } else if (prop.parameterType === "output") {
            const param = moddle.create("camunda:OutputParameter", {
              name: prop.id,
              value: prop.default || "",
            });
            outputParams.push(param);
          }
        });

        // Create InputOutput element if we have parameters
        if (inputParams.length > 0 || outputParams.length > 0) {
          const inputOutput = moddle.create("camunda:InputOutput", {
            inputParameters: inputParams,
            outputParameters: outputParams,
          });

          // Connector templates need the inputs nested inside a
          // `<camunda:connector>` element with a `<camunda:connectorId>`.
          // Java-delegate templates keep `<camunda:inputOutput>` as a
          // direct child of `<bpmn:extensionElements>` (the original
          // shape).
          const extensionChild = isConnectorTemplate
            ? moddle.create("camunda:Connector", {
                connectorId,
                inputOutput,
              })
            : inputOutput;

          const extensionElements = moddle.create("bpmn:ExtensionElements", {
            values: [extensionChild],
          });

          businessObject.extensionElements = extensionElements;
        }

        // Store custom properties reference on the business object using set method
        // Store as JSON string since BPMN attributes are strings
        businessObject.set("custom:properties", JSON.stringify(properties));
      }

      // Create the shape
      const shape = elementFactory.createShape({
        type: "bpmn:ServiceTask",
        businessObject: businessObject,
      });

      // Add some randomness to position to avoid overlapping
      const offsetX = (Math.random() - 0.5) * 100;
      const offsetY = (Math.random() - 0.5) * 100;

      // Create the element on the canvas
      modeling.createShape(
        shape,
        { x: centerX + offsetX, y: centerY + offsetY },
        rootElement,
      );

      // Select the newly created element
      const selection = modelerRef.current.get("selection") as any;
      selection.select(shape);

      toast.success(`Added ${taskName}`);
    };

    // Create Pool/Lane
    const createPool = () => {
      createBpmnElement("bpmn:Participant");
      setActiveDropdown(null);
    };

    // Create Gateways
    const createExclusiveGateway = () => {
      createBpmnElement("bpmn:ExclusiveGateway");
      setActiveDropdown(null);
    };
    const createParallelGateway = () => {
      createBpmnElement("bpmn:ParallelGateway");
      setActiveDropdown(null);
    };
    const createInclusiveGateway = () => {
      createBpmnElement("bpmn:InclusiveGateway");
      setActiveDropdown(null);
    };
    const createEventBasedGateway = () => {
      createBpmnElement("bpmn:EventBasedGateway");
      setActiveDropdown(null);
    };

    // Create Intermediate Events with proper event definitions
    const createMessageIntermediateCatch = () => {
      createEventWithDefinition(
        "bpmn:IntermediateCatchEvent",
        "bpmn:MessageEventDefinition",
      );
      setActiveDropdown(null);
    };
    const createMessageIntermediateThrow = () => {
      createEventWithDefinition(
        "bpmn:IntermediateThrowEvent",
        "bpmn:MessageEventDefinition",
      );
      setActiveDropdown(null);
    };
    const createTimerIntermediateCatch = () => {
      createEventWithDefinition(
        "bpmn:IntermediateCatchEvent",
        "bpmn:TimerEventDefinition",
      );
      setActiveDropdown(null);
    };
    const createConditionalIntermediateCatch = () => {
      createEventWithDefinition(
        "bpmn:IntermediateCatchEvent",
        "bpmn:ConditionalEventDefinition",
      );
      setActiveDropdown(null);
    };
    const createSignalIntermediateCatch = () => {
      createEventWithDefinition(
        "bpmn:IntermediateCatchEvent",
        "bpmn:SignalEventDefinition",
      );
      setActiveDropdown(null);
    };
    const createSignalIntermediateThrow = () => {
      createEventWithDefinition(
        "bpmn:IntermediateThrowEvent",
        "bpmn:SignalEventDefinition",
      );
      setActiveDropdown(null);
    };

    // Ref for the dropdown container to detect outside clicks
    const dropdownContainerRef = useRef<HTMLDivElement>(null);

    // Toggle dropdown
    const toggleDropdown = useCallback((dropdown: string) => {
      setActiveDropdown((prev) => (prev === dropdown ? null : dropdown));
    }, []);

    // Import BPMN XML into the editor
    const handleImportXml = useCallback(async (xml: string) => {
      if (!modelerRef.current) return;
      try {
        // Heal legacy connector-template tasks on user-driven import too,
        // not only on initial load. Matches the path above.
        const xmlForImport = migrateLegacyConnectorTemplates(xml);
        await modelerRef.current.importXML(xmlForImport);
        const canvas = modelerRef.current.get("canvas") as any;
        canvas.zoom("fit-viewport", "auto");
        toast.success("BPMN file imported successfully");
      } catch (error) {
        console.error("Error importing BPMN:", error);
        toast.error("Failed to import BPMN file");
      }
    }, []);

    // Export BPMN XML - downloads as file
    const handleExportXml = useCallback(async () => {
      if (!modelerRef.current) return;
      try {
        const raw = await modelerRef.current.saveXML({ format: true });
        // Also sanitize the downloadable copy so anyone who reuses it
        // (e.g. importing into a fresh modeler / hand-editing) doesn't
        // re-introduce the deploy warnings.
        const xml = raw.xml ? sanitizeBpmnXml(raw.xml) : raw.xml;
        if (xml) {
          const blob = new Blob([xml], { type: "application/xml" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${workflow?.nameSlug || "diagram"}.bpmn`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          toast.success("BPMN file exported successfully");
        }
      } catch (error) {
        console.error("Error exporting BPMN:", error);
        toast.error("Failed to export BPMN file");
      }
    }, [workflow?.nameSlug]);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          activeDropdown &&
          dropdownContainerRef.current &&
          !dropdownContainerRef.current.contains(event.target as Node)
        ) {
          setActiveDropdown(null);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [activeDropdown]);

    // Expose save function via ref
    useImperativeHandle(
      ref,
      () => ({
        save: handleSave,
        getXml: handleGetXml,
        refresh: handleRefresh,
        importXml: handleImportXml,
        exportXml: handleExportXml,
        isSaving,
      }),
      [
        handleSave,
        handleGetXml,
        handleRefresh,
        handleImportXml,
        handleExportXml,
        isSaving,
      ],
    );

    // Always render the canvas, but show loading overlay
    return (
      <div className="flex h-full bg-gray-50 relative">
        {/* Left Sidebar - Add Object Panel */}
        <div
          className={`bg-white border-r border-gray-200 flex flex-col overflow-hidden transition-all duration-300 ${
            isLeftPanelOpen ? "w-56" : "w-0"
          }`}
        >
          {/* Panel Header - Tabs */}
          <div className="border-b border-gray-200 min-w-[224px]">
            <div className="flex items-center">
              <button
                onClick={() => setActiveTab("addObject")}
                className={`flex-1 px-3 py-3 text-sm font-medium transition-colors ${
                  activeTab === "addObject"
                    ? "text-gray-900 border-b-2 border-gray-900"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Pinned
              </button>
              <button
                onClick={() => setActiveTab("customConnectors")}
                className={`flex-1 px-3 py-3 text-sm font-medium transition-colors ${
                  activeTab === "customConnectors"
                    ? "text-gray-900 border-b-2 border-gray-900"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Components
              </button>
              <button
                onClick={() => setIsLeftPanelOpen(false)}
                className="p-2 hover:bg-gray-100 rounded transition-colors text-gray-400 mr-1"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="p-3 min-w-[224px]">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search Objects"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>

          {/* Panel Content */}
          <div className="flex-1 overflow-y-auto min-w-[224px]">
            {activeTab === "addObject" ? (
              <>
                {/* Pinned Connectors Section */}
                <div className="px-3 py-2 border-t border-gray-100">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Pinned Connectors
                  </h4>
                  {isLoadingTemplates ? (
                    <div className="text-xs text-gray-400 italic px-2">
                      Loading templates...
                    </div>
                  ) : connectorTemplates.filter((t) =>
                      pinnedConnectorIds.includes(t.id),
                    ).length === 0 ? (
                    <div className="text-xs text-gray-400 italic px-2 py-4 text-center border border-dashed border-gray-200 rounded-lg">
                      Pin connectors from the Connectors tab to see them here
                    </div>
                  ) : (
                    <div className="space-y-0.5">
                      {connectorTemplates
                        .filter((t) => pinnedConnectorIds.includes(t.id))
                        .map((template) => (
                          <div key={template.id} className="relative group">
                            <button
                              onClick={() =>
                                createServiceTaskFromTemplate(template)
                              }
                              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors text-left pr-8"
                              title={
                                template.decodedContent?.topic || template.name
                              }
                            >
                              {template.decodedContent?.modelerTemplateIcon ? (
                                <img
                                  src={
                                    template.decodedContent.modelerTemplateIcon
                                  }
                                  alt={template.name}
                                  className="w-5 h-5 object-contain"
                                  onError={(e) => {
                                    (
                                      e.target as HTMLImageElement
                                    ).style.display = "none";
                                  }}
                                />
                              ) : (
                                <span className="w-5 h-5 flex items-center justify-center text-gray-400">
                                  <Settings size={16} />
                                </span>
                              )}
                              <span className="text-sm text-gray-700 truncate">
                                {template.name}
                              </span>
                            </button>
                            <button
                              onClick={(e) =>
                                toggleConnectorPin(template.id, e)
                              }
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-indigo-500 hover:text-indigo-700 opacity-0 group-hover:opacity-100 transition-opacity p-1"
                              title="Unpin"
                            >
                              <Pin size={14} className="fill-current" />
                            </button>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="px-3 py-2">
                {isLoadingTemplates ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                    <span className="ml-2 text-sm text-gray-500">
                      Loading connectors...
                    </span>
                  </div>
                ) : connectorTemplates.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No connectors available
                  </div>
                ) : (
                  // Group templates by category
                  Object.entries(
                    connectorTemplates
                      .filter(
                        (t) =>
                          t.name
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          t.category
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase()),
                      )
                      .reduce(
                        (acc, template) => {
                          const category = template.category || "other";
                          if (!acc[category]) acc[category] = [];
                          acc[category].push(template);
                          return acc;
                        },
                        {} as Record<string, DesignerTemplate[]>,
                      ),
                  ).map(([category, templates]) => (
                    <div key={category} className="mb-4">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 capitalize">
                        {category}
                      </h4>
                      <div className="space-y-0.5">
                        {templates.map((template) => {
                          const isPinned = pinnedConnectorIds.includes(
                            template.id,
                          );
                          return (
                            <div key={template.id} className="relative group">
                              <button
                                onClick={() =>
                                  createServiceTaskFromTemplate(template)
                                }
                                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors text-left pr-8"
                                title={
                                  template.decodedContent?.topic ||
                                  template.name
                                }
                              >
                                {template.decodedContent
                                  ?.modelerTemplateIcon ? (
                                  <img
                                    src={
                                      template.decodedContent
                                        .modelerTemplateIcon
                                    }
                                    alt={template.name}
                                    className="w-5 h-5 object-contain"
                                    onError={(e) => {
                                      // Fallback to a default icon if image fails to load
                                      (
                                        e.target as HTMLImageElement
                                      ).style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <span className="w-5 h-5 flex items-center justify-center text-gray-400">
                                    <Settings size={16} />
                                  </span>
                                )}
                                <span className="text-sm text-gray-700 truncate">
                                  {template.name}
                                </span>
                              </button>
                              <button
                                onClick={(e) =>
                                  toggleConnectorPin(template.id, e)
                                }
                                className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 transition-all ${isPinned ? "text-indigo-500 opacity-100" : "text-gray-300 opacity-0 group-hover:opacity-100 hover:text-indigo-500"}`}
                                title={isPinned ? "Unpin" : "Pin"}
                              >
                                <Pin
                                  size={14}
                                  className={isPinned ? "fill-current" : ""}
                                />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Main Canvas Area */}
        <div className="flex-1 flex flex-col overflow-hidden relative">
          {/* Canvas Container */}
          <div className="flex-1 relative bg-white m-3 mb-0 rounded-t-lg border border-gray-200 border-b-0 bg-[linear-gradient(#f0f0f0_1px,transparent_1px),linear-gradient(90deg,#f0f0f0_1px,transparent_1px)] bg-[length:20px_20px] bg-[-1px_-1px]">
            {/* Loading Overlay */}
            {isLoading && (
              <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10 rounded-t-lg">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 font-medium">
                    Loading BPMN Editor...
                  </p>
                  {workflow?.name && (
                    <p className="text-gray-400 text-sm mt-2">
                      Loading: {workflow.name}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Error Overlay */}
            {error && (
              <div className="absolute inset-0 bg-white flex items-center justify-center z-10 rounded-t-lg">
                <div className="text-center">
                  <div className="text-red-500 mb-4">
                    <svg
                      className="w-16 h-16 mx-auto"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-red-600 font-medium mb-2">
                    Failed to load BPMN Editor
                  </p>
                  <p className="text-gray-600 text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* Canvas - always in DOM */}
            <div ref={canvasRef} className="w-full h-full" />
          </div>

          {/* Bottom Toolbar */}
          <div className="bg-white border border-gray-200 mx-3 mb-3 rounded-b-lg px-4 py-2 flex items-center justify-between">
            {/* Left Section - Minimap & Zoom */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsLeftPanelOpen(!isLeftPanelOpen)}
                className={`p-2 rounded-lg transition-colors ${isLeftPanelOpen ? "bg-gray-100 text-gray-700" : "hover:bg-gray-100 text-gray-500"}`}
              >
                <LayoutGrid size={18} />
              </button>
              <div className="flex items-center gap-1 text-gray-600">
                <button
                  onClick={handleZoomIn}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                >
                  <Plus size={16} />
                </button>
                <span className="text-sm font-medium min-w-[50px] text-center">
                  {zoomLevel} %
                </span>
                <button
                  onClick={handleZoomOut}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                >
                  <Minus size={16} />
                </button>
              </div>
            </div>

            {/* Center Section - Undo/Redo & Shape Palette */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <button
                  onClick={handleUndo}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
                  title="Undo"
                >
                  <Undo size={18} />
                </button>
                <button
                  onClick={handleRedo}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
                  title="Redo"
                >
                  <Redo size={18} />
                </button>
              </div>

              <div className="h-6 w-px bg-gray-200" />

              {/* Shape Palette with Dropdowns */}
              <div
                ref={dropdownContainerRef}
                className="flex items-center gap-2 relative"
              >
                {/* Start Event Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => toggleDropdown("startEvent")}
                    className={`w-6 h-6 rounded-full border-2 border-teal-400 hover:ring-2 hover:ring-teal-200 transition-all relative ${activeDropdown === "startEvent" ? "ring-2 ring-teal-200" : ""}`}
                    title="Add Start Event"
                  />
                  {/* Black dot indicator */}
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-gray-800 rounded-full"></span>
                  {activeDropdown === "startEvent" && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[200px] z-50">
                      <button
                        onClick={createMessageStartEvent}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-start-event-message text-base text-gray-500"></span>
                        Message start event
                      </button>
                      <button
                        onClick={createTimerStartEvent}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-start-event-timer text-base text-gray-500"></span>
                        Timer start event
                      </button>
                      <button
                        onClick={createConditionalStartEvent}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-start-event-condition text-base text-gray-500"></span>
                        Conditional start event
                      </button>
                      <button
                        onClick={createSignalStartEvent}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-start-event-signal text-base text-gray-500"></span>
                        Signal start event
                      </button>
                    </div>
                  )}
                </div>

                {/* Intermediate Event Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => toggleDropdown("intermediateEvent")}
                    className={`w-6 h-6 rounded-full border-2 border-yellow-400 hover:ring-2 hover:ring-yellow-200 transition-all relative ${activeDropdown === "intermediateEvent" ? "ring-2 ring-yellow-200" : ""}`}
                    title="Add Intermediate Event"
                  />
                  {/* Black dot indicator */}
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-gray-800 rounded-full"></span>
                  {activeDropdown === "intermediateEvent" && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[260px] z-50">
                      <button
                        onClick={createMessageIntermediateCatch}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-intermediate-event-catch-message text-base text-gray-500"></span>
                        Message intermediate catch event
                      </button>

                      <button
                        onClick={createMessageIntermediateThrow}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-intermediate-event-throw-message text-base text-gray-500"></span>
                        Message intermediate throw event
                      </button>
                      <button
                        onClick={createTimerIntermediateCatch}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-intermediate-event-catch-timer text-base text-gray-500"></span>
                        Timer intermediate catch event
                      </button>
                      <button
                        onClick={createConditionalIntermediateCatch}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-intermediate-event-catch-condition text-base text-gray-500"></span>
                        Conditional intermediate catch ev...
                      </button>
                      <button
                        onClick={createSignalIntermediateCatch}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-intermediate-event-catch-signal text-base text-gray-500"></span>
                        Signal intermediate catch event
                      </button>
                      <button
                        onClick={createSignalIntermediateThrow}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-intermediate-event-throw-signal text-base text-gray-500"></span>
                        Signal intermediate throw event
                      </button>
                    </div>
                  )}
                </div>

                {/* End Event Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => toggleDropdown("endEvent")}
                    className={`w-6 h-6 rounded-full border-2 border-red-400 hover:ring-2 hover:ring-red-200 transition-all relative ${activeDropdown === "endEvent" ? "ring-2 ring-red-200" : ""}`}
                    title="Add End Event"
                  />
                  {/* Black dot indicator */}
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-gray-800 rounded-full"></span>
                  {activeDropdown === "endEvent" && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[200px] z-50">
                      <button
                        onClick={createMessageEndEvent}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-end-event-message text-base text-gray-500"></span>
                        Message end event
                      </button>
                      <button
                        onClick={createSignalEndEvent}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-end-event-signal text-base text-gray-500"></span>
                        Signal end event
                      </button>
                      <button
                        onClick={createTerminateEndEvent}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-end-event-terminate text-base text-gray-500"></span>
                        Terminate end event
                      </button>
                    </div>
                  )}
                </div>

                {/* Task Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => toggleDropdown("task")}
                    className={`w-6 h-6 border border-gray-400 rounded hover:bg-gray-100 transition-colors relative ${activeDropdown === "task" ? "bg-gray-50" : ""}`}
                    title="Add Task"
                  />
                  {/* Black dot indicator */}
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-gray-800 rounded-full"></span>
                  {activeDropdown === "task" && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[200px] z-50 max-h-80 overflow-y-auto">
                      <button
                        onClick={createUserTask}
                        className="w-full flex items-center gap-3 px-4 py-2 bg-teal-50 text-left text-sm text-teal-700"
                      >
                        <span className="bpmn-icon-user-task text-base text-teal-500"></span>
                        User task
                      </button>
                      <button
                        onClick={createServiceTask}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-service-task text-base text-gray-500"></span>
                        Service task
                      </button>
                      <button
                        onClick={createSendTask}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-send-task text-base text-gray-500"></span>
                        Send task
                      </button>
                      <button
                        onClick={createReceiveTask}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-receive-task text-base text-gray-500"></span>
                        Receive task
                      </button>
                      <button
                        onClick={createManualTask}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-manual-task text-base text-gray-500"></span>
                        Manual task
                      </button>
                      <button
                        onClick={createBusinessRuleTask}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-business-rule-task text-base text-gray-500"></span>
                        Business rule task
                      </button>
                      <button
                        onClick={createScriptTask}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-script-task text-base text-gray-500"></span>
                        Script task
                      </button>
                      <button
                        onClick={createCallActivity}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-call-activity text-base text-gray-500"></span>
                        Call activity
                      </button>
                      <button
                        onClick={createSubProcess}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-subprocess-collapsed text-base text-gray-500"></span>
                        Sub-process (collapsed)
                      </button>
                    </div>
                  )}
                </div>

                {/* Gateway Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => toggleDropdown("gateway")}
                    className={`p-0.5 rounded transition-colors text-gray-500 relative ${activeDropdown === "gateway" ? "bg-gray-100" : "hover:bg-gray-100"}`}
                    title="Add Gateway"
                  >
                    <Diamond size={18} />
                    {/* Black dot indicator */}
                    <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-gray-800 rounded-full"></span>
                  </button>
                  {activeDropdown === "gateway" && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[180px] z-50">
                      <button
                        onClick={createParallelGateway}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-gateway-parallel text-base text-gray-500"></span>
                        Parallel Gateway
                      </button>
                      <button
                        onClick={createExclusiveGateway}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-gateway-xor text-base text-gray-500"></span>
                        Exclusive Gateway
                      </button>
                      <button
                        onClick={createInclusiveGateway}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-gateway-or text-base text-gray-500"></span>
                        Inclusive Gateway
                      </button>
                      <button
                        onClick={createEventBasedGateway}
                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left text-sm text-gray-700"
                      >
                        <span className="bpmn-icon-gateway-eventbased text-base text-gray-500"></span>
                        Event Based Gateway
                      </button>
                    </div>
                  )}
                </div>

                {/* Pool/Lane Button */}
                <button
                  className="w-6 h-6 border border-gray-400 rounded hover:bg-gray-100 transition-colors flex items-center justify-center"
                  title="Add Pool/Lane"
                  onClick={createPool}
                >
                  <Columns size={14} className="text-gray-500" />
                </button>
              </div>

              <div className="h-6 w-px bg-gray-200" />

              <button
                onClick={createTask}
                className="p-1.5 bg-teal-500 text-white rounded hover:bg-teal-600 transition-colors"
                title="Quick Add Task"
              >
                <Plus size={16} />
              </button>
            </div>

            {/* Right Section - Toggle Right Panel */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsRightPanelOpen(!isRightPanelOpen)}
                className={`p-2 rounded-lg transition-colors ${isRightPanelOpen ? "bg-gray-100 text-gray-700" : "hover:bg-gray-100 text-gray-500"}`}
                title={
                  isRightPanelOpen ? "Hide Configuration" : "Show Configuration"
                }
              >
                <Settings size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Right Properties Panel */}
        <div
          className={`bg-white border-l border-gray-200 flex flex-col overflow-hidden transition-all duration-300 ${
            isRightPanelOpen ? "w-80" : "w-0"
          }`}
        >
          {/* Determine if we're showing connector tabs, UserTask tabs, or standard header */}
          {isConnectorTask(selectedElement) ? (
            // Tabs Header for connector elements
            <div className="border-b border-gray-200 min-w-[320px]">
              <div className="flex px-4 pt-3">
                <button
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    rightPanelTab === "properties"
                      ? "text-blue-600 border-blue-600"
                      : "text-gray-500 border-transparent hover:text-gray-700"
                  }`}
                  onClick={() => setRightPanelTab("properties")}
                >
                  {selectedElement?.businessObject?.name || "Connector"}
                </button>
                <button
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    rightPanelTab === "element"
                      ? "text-blue-600 border-blue-600"
                      : "text-gray-500 border-transparent hover:text-gray-700"
                  }`}
                  onClick={() => setRightPanelTab("element")}
                >
                  Configuration
                </button>
              </div>
            </div>
          ) : selectedElement?.businessObject?.$type === "bpmn:UserTask" ||
            selectedElement?.businessObject?.$type === "bpmn:StartEvent" ? (
            // Tabs Header for UserTask/StartEvent elements
            <div className="border-b border-gray-200 min-w-[320px]">
              <div className="flex px-4 pt-3">
                <button
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    rightPanelTab === "properties"
                      ? "text-indigo-600 border-indigo-600"
                      : "text-gray-500 border-transparent hover:text-gray-700"
                  }`}
                  onClick={() => setRightPanelTab("properties")}
                >
                  DoIT Forms
                </button>
                <button
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    rightPanelTab === "element"
                      ? "text-indigo-600 border-indigo-600"
                      : "text-gray-500 border-transparent hover:text-gray-700"
                  }`}
                  onClick={() => setRightPanelTab("element")}
                >
                  Configuration
                </button>
              </div>
            </div>
          ) : selectedElement?.businessObject?.$type === "bpmn:ScriptTask" ? (
            // Tabs Header for ScriptTask elements
            <div className="border-b border-gray-200 min-w-[320px]">
              <div className="flex px-4 pt-3">
                <button
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    rightPanelTab === "properties"
                      ? "text-amber-600 border-amber-600"
                      : "text-gray-500 border-transparent hover:text-gray-700"
                  }`}
                  onClick={() => setRightPanelTab("properties")}
                >
                  Script
                </button>
                <button
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    rightPanelTab === "element"
                      ? "text-amber-600 border-amber-600"
                      : "text-gray-500 border-transparent hover:text-gray-700"
                  }`}
                  onClick={() => setRightPanelTab("element")}
                >
                  Configuration
                </button>
              </div>
            </div>
          ) : (
            // Standard header for non-connector elements
            <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between min-w-[320px]">
              <h3 className="text-sm font-semibold text-gray-900">
                Configuration
              </h3>
              <button
                onClick={() => setIsRightPanelOpen(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors text-gray-400"
              >
                <X size={16} />
              </button>
            </div>
          )}

          {/* Custom Properties Panel - shows for connector elements, UserTask/StartEvent, or ScriptTask on properties tab */}
          {(isConnectorTask(selectedElement) && rightPanelTab === "properties") ||
          ((selectedElement?.businessObject?.$type === "bpmn:UserTask" ||
            selectedElement?.businessObject?.$type === "bpmn:StartEvent" ||
            selectedElement?.businessObject?.$type === "bpmn:ScriptTask") &&
            rightPanelTab === "properties") ? (
            <CustomPropertiesPanel
              modeler={modelerRef.current}
              selectedElement={selectedElement}
            />
          ) : null}

          {/* Standard Properties Panel - hidden when custom panel tab is active */}
          <div
            ref={propertiesPanelRef}
            className={`flex-1 overflow-y-auto properties-panel-container ${
              (isConnectorTask(selectedElement) && rightPanelTab === "properties") ||
              ((selectedElement?.businessObject?.$type === "bpmn:UserTask" ||
                selectedElement?.businessObject?.$type === "bpmn:StartEvent" ||
                selectedElement?.businessObject?.$type === "bpmn:ScriptTask") &&
                rightPanelTab === "properties")
                ? "hidden"
                : ""
            }`}
          />
        </div>
      </div>
    );
  },
);

SimpleBpmnEditor.displayName = "SimpleBpmnEditor";

export default SimpleBpmnEditor;
